import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../store';
import { api } from '../../utils/api';
import ChatPanel, { kindKey, useChatPanelOpen } from './ChatPanel';
import type { ChatNoticeMessage } from '../../types';

// ── Chat notices, step 3 of 3 (report 32621dcf) ──────────────────────────────────────────────────────────────────
// The reporter: "New messages can reach somebody by having a notification pop up on the screen inside either the
// main portal or the user portal." Steps 1 and 2 left a message showing only as the red number on the Chat button,
// which nobody sees unless they look at it, so nothing popped up. These are those pop-ups, built as the new-mail
// ones are (components/NewMailToast.tsx): a card for each message that arrives, in the corner of the page, with who
// wrote it, which chat it came in and its first words. Pressing one opens the chat pop-up (ChatPanel) on that chat,
// over whatever page is showing; the × or about half a minute sends it away.
// They are shown on the two screens the reporter named — the main portal's Dashboard (components/Portal) and My
// Dashboard(s) (components/FocusDashboard) — which are also the only two screens the Chat button itself is on.
// The corner: the BOTTOM LEFT, because the new-mail cards own the bottom right (NewMailToast: right 24, bottom 92)
// and two stacks in one corner would be drawn over each other. So mail is on the right, chat on the left, and each
// kind of notice is recognisable before it is read.
// They keep quiet while a chat pop-up is open (useChatPanelOpen): whatever arrives is being read in it already.
// What counts as new, and this person's own on/off switch (the bell in that pop-up's header), are the server's
// call: GET /api/chat/notify, which answers only what the reader has not read, by the same watermark the red
// bubble counts.
//
// Only the tab in front asks. The browser's tabs share one marker and one list of the messages already shown
// (localStorage), so with several SLF tabs open a message pops up once, in the tab being looked at, and a tab
// opened meanwhile carries on from the others' marker rather than skipping what came in just before it. When no tab
// has asked for STALE_MS, the first ask starts afresh instead: the morning's first page doesn't pop up the night's
// messages, and neither does closing a chat window that was left open all afternoon.

const POLL_MS = 30_000;          // the red bubble's own cadence, so the card and the number appear together
const CARD_MS = 30_000;          // how long a card stays
const AFTER_HOVER_MS = 4_000;    // once the mouse leaves it
const FADE_MS = 400;
const STALE_MS = 15 * 60_000;
const MAX_CARDS = 5;
const SEEN_KEEP = 100;

type Shared = { since: number; at: number; seen: string[] };   // at = when a tab last asked (this browser's clock)
const sharedKey = (userId: string) => `mf_chatNotice:${userId}`;
function readShared(userId: string): Shared | null {
  try {
    const v = JSON.parse(localStorage.getItem(sharedKey(userId)) || 'null');
    return v && Number.isFinite(v.since) && Number.isFinite(v.at) && Array.isArray(v.seen) ? v : null;
  } catch { return null; }
}
function writeShared(userId: string, v: Shared): void {
  try { localStorage.setItem(sharedKey(userId), JSON.stringify(v)); } catch { /* storage full — this tab still works */ }
}

export default function ChatToast() {
  const me = useStore(s => s.me);
  const userId = me?.userId;
  const panelOpen = useChatPanelOpen();
  const [cards, setCards] = useState<ChatNoticeMessage[]>([]);
  const [openAt, setOpenAt] = useState('');   // the chat a card was pressed on; '' = this stack has no pop-up open
  const busy = useRef(false);

  const poll = useCallback(async () => {
    if (!userId || busy.current || document.visibilityState !== 'visible' || !localStorage.getItem('mf_token')) return;
    busy.current = true;
    try {
      const prev = readShared(userId);
      const carryOn = !!prev && Date.now() - prev.at < STALE_MS;
      const res = await api.chatNotify(carryOn ? prev!.since : undefined);
      // Read again: another tab may have shown some of these while this one was asking.
      const seen = carryOn ? (readShared(userId)?.seen || prev!.seen) : [];
      const fresh = res.on ? (res.messages || []).filter(m => !seen.includes(m.id)) : [];
      writeShared(userId, { since: res.since, at: Date.now(), seen: [...seen, ...fresh.map(m => m.id)].slice(-SEEN_KEEP) });
      if (fresh.length) {
        // Newest first from the server; the stack runs oldest at the top to newest at the bottom.
        const add = fresh.slice().reverse();
        setCards(cs => [...cs, ...add.filter(a => !cs.some(c => c.id === a.id))].slice(-MAX_CARDS));
      }
    } catch { /* transient — the next ask tries again */ }
    finally { busy.current = false; }
  }, [userId]);

  // At once, every POLL_MS, and again the moment this tab comes to the front. Nothing is asked while a chat pop-up
  // is open; closing it asks again, and only what the reader still has not read can come back as a card — so a
  // message read in that pop-up never pops up after it, while one that arrived in another chat meanwhile does. A
  // pop-up left open longer than STALE_MS starts the marker afresh, so an afternoon of messages is never emptied
  // onto the screen at once; the red number on the Chat button still shows them.
  useEffect(() => {
    if (!userId || panelOpen) return;
    void poll();
    const timer = window.setInterval(() => { void poll(); }, POLL_MS);
    const onShow = () => { if (document.visibilityState === 'visible') void poll(); };
    document.addEventListener('visibilitychange', onShow);
    return () => { window.clearInterval(timer); document.removeEventListener('visibilitychange', onShow); };
  }, [userId, panelOpen, poll]);

  // A pop-up is open, here or from a button: the cards go, so none is left sitting behind it to reappear on close.
  useEffect(() => { if (panelOpen) setCards([]); }, [panelOpen]);

  const drop = (id: string) => setCards(cs => cs.filter(c => c.id !== id));
  // A card opens its own chat — except a job lead's card from the Active Jobs group when they are not in that group
  // (`notice`, report b6fccf12): the group would refuse them, and a refused call pages the admins, so pressing it only
  // sends it away.
  const press = (c: ChatNoticeMessage) => {
    if (c.notice) { drop(c.id); return; }
    setOpenAt(c.convId);
    setCards([]);
  };

  // Under the dialogs (5400 and up) and Joy (9000); over the page and the toast line (2000). The empty stack lets
  // clicks through.
  return (
    <>
      <div aria-live="polite" style={{ position: 'fixed', left: 24, bottom: 92, zIndex: 4500, width: 320, maxWidth: 'calc(100vw - 48px)', display: 'flex', flexDirection: 'column', gap: 8, pointerEvents: 'none' }}>
        {cards.map(c => (
          <ChatCard key={c.id} card={c}
            onOpen={() => press(c)}
            onClose={() => drop(c.id)} />
        ))}
      </div>
      {/* The chat pop-up a card opens, on that card's chat and over whatever page is showing. The page's own Chat
          button keeps its own pop-up; only one of the two is ever on screen, because a card can only be pressed
          while none is open. `: null`, not `&&`: this sits in the portal's menu row, where an empty string would
          be one more thing for that row to space out. */}
      {openAt ? <ChatPanel startId={openAt} onClose={() => setOpenAt('')} /> : null}
    </>
  );
}

function ChatCard({ card, onOpen, onClose }: { card: ChatNoticeMessage; onOpen: () => void; onClose: () => void }) {
  const { t } = useTranslation();
  const [leaving, setLeaving] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  const fadeAfter = (ms: number) => { window.clearTimeout(timer.current); timer.current = window.setTimeout(() => setLeaving(true), ms); };
  useEffect(() => { fadeAfter(CARD_MS); return () => window.clearTimeout(timer.current); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Gone once the fade has run; the mouse coming back over it in time brings it back.
  useEffect(() => {
    if (!leaving) return;
    const id = window.setTimeout(onClose, FADE_MS);
    return () => window.clearTimeout(id);
  }, [leaving]); // eslint-disable-line react-hooks/exhaustive-deps

  // A group names itself beside "New message", as a mail card names the mailbox; a one-to-one chat is named by the
  // person who wrote, which is already the line below.
  const group = card.convKind === 'group' ? card.convName : '';
  // Text is its own first words. The other three kinds are named — Photo, Voice note, File — with whatever caption
  // was sent after them. A job lead's card (report b6fccf12) has no words at all: only that there is a new comment on
  // their job for them.
  const words = card.leadNotice ? t('chat_notify_job_lead', { job: card.leadNotice.jobNo })
    : card.kind === 'text' ? card.preview
    : card.preview ? `${t(kindKey(card.kind))} · ${card.preview}` : t(kindKey(card.kind));

  return (
    <div role="status"
      onMouseEnter={() => { window.clearTimeout(timer.current); setLeaving(false); }}
      onMouseLeave={() => fadeAfter(AFTER_HOVER_MS)}
      style={{ pointerEvents: 'auto', display: 'flex', alignItems: 'flex-start', gap: 6, padding: '10px 8px 10px 12px', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderLeft: '4px solid var(--accent)', borderRadius: 12, boxShadow: 'var(--shadow-md)', animation: 'slideUp 0.25s ease', opacity: leaving ? 0 : 1, transition: `opacity ${FADE_MS}ms ease` }}>
      <button onClick={onOpen} title={card.notice ? t('chat_notify_close') : t('chat_notify_open')}
        style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'flex-start', gap: 10, padding: 0, border: 'none', background: 'transparent', textAlign: 'left', cursor: 'pointer', color: 'inherit', font: 'inherit' }}>
        <span style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--accent-bg)', color: 'var(--accent-text)' }}>
          <i className={`ti ti-${card.convKind === 'group' ? 'users' : 'message-circle'}`} style={{ fontSize: 16 }} aria-hidden />
        </span>
        <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--accent-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {t('chat_notify_new')}
            {group && ` · ${group}`}
          </span>
          {/* Who wrote, in that person's own colour — the colour their name wears everywhere else in SLF. The first
              words below it are the ordinary grey, as the chat pop-up's own words are black (reports c05bc176,
              b4806261, now 2ad9e77c: "The user's name in their user color, just not the chat messages themselves").
              So the colour on a card says who wrote, never what was written. */}
          <span style={{ fontSize: 13, fontWeight: 700, color: card.senderColor || 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{card.senderName}</span>
          <span style={{ fontSize: 12, lineHeight: 1.4, color: 'var(--text-secondary)', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', wordBreak: 'break-word' }}>{words}</span>
        </span>
      </button>
      <button onClick={onClose} title={t('chat_notify_close')} aria-label={t('chat_notify_close')}
        style={{ flexShrink: 0, width: 22, height: 22, padding: 0, borderRadius: 6, border: 'none', background: 'transparent', color: 'var(--text-tertiary)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <i className="ti ti-x" style={{ fontSize: 14 }} aria-hidden />
      </button>
    </div>
  );
}
